import React, { useState } from 'react';
import useImageAspect from '../../utils/useImageAspect';
import { aspectFits } from '../../utils/boardPieces';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  isDoneStatus,
  portraitGridShape,
  splitVisible,
  contributorNames,
  buildSlots,
  slotImage,
  pickRevealContributor,
} from '../../utils/tileContributors';

/** What one player's portrait shows in the GRID (undivided, shared piece) layout. */
function portraitSrc(userState, { masterSrc, useMasterFallback, previews }) {
  const isDone = isDoneStatus(userState?.status);
  const finished = isDone && userState?.tileImageKey ? s3PublicUrl(userState.tileImageKey) : null;
  const live = !isDone ? previews[userState?.key] || null : null;
  const fallback = useMasterFallback && !isDone ? masterSrc : null;
  return finished || live || fallback || null;
}

/**
 * A rectangle (fractions of the piece) cut out of a WHOLE-piece image: the image
 * is laid out at piece size inside an overflow-hidden box that is just the slice.
 */
function CroppedImage({ src, rect, dim }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      className={`live-canvas-slot-crop${dim ? ' live-canvas-tile-outline-img--waiting' : ''}`}
      style={{
        width: `${100 / rect.w}%`,
        height: `${100 / rect.h}%`,
        left: `${(-rect.x / rect.w) * 100}%`,
        top: `${(-rect.y / rect.h) * 100}%`,
      }}
    />
  );
}

/**
 * A player's finished tile. If its shape no longer matches the cell (the artwork
 * was re-pieced after it was saved) it would be drawn misaligned, so the reference
 * image is shown in its place instead.
 */
function PlayerImage({ src, expectedAspect, fallback, className, guard = true }) {
  // Which src was measured, and whether its shape fits. Until it is measured the
  // reference image is shown, so a wrong-shaped tile never flashes on screen.
  const [verdict, setVerdict] = useState({ src: null, ratio: null });
  const plain = <img src={src} alt="" aria-hidden className={className} />;
  if (!guard || !fallback) return plain;
  const measured = verdict.src === src && verdict.ratio > 0;
  // The cell's own shape may still be loading: stay on the reference until both are known.
  if (measured && expectedAspect > 0 && aspectFits(verdict.ratio, expectedAspect)) return plain;
  return (
    <>
      {fallback}
      {measured ? null : (
        <img
          src={src}
          alt=""
          aria-hidden
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            setVerdict({ src, ratio: w && h ? w / h : null });
          }}
        />
      )}
    </>
  );
}

/**
 * One board piece.
 *  - `contributors`: EVERY player on this piece.
 *  - `previews`: { [firebaseUserKey]: dataUrl } — only the players THIS viewer is
 *    watching (not everyone), so live drawing is shown for a per-viewer sample.
 * Divided pieces (sub_count > 1) render each player's slice at its exact position;
 * otherwise shared pieces fall back to a grid of portraits.
 */
export default function LiveCanvasTile({
  outlineSrc,
  masterSrc = null,
  contributors = [],
  previews = {},
  isMine = false,
  useMasterFallback = false,
  cellAspect: cellAspectProp = null,
  measureOutline = true,
  finalView = false,
  currentUserId = null,
  representative,
  tileId = null,
}) {
  const hasPlayer = contributors.length > 0;
  // The board measures ONE piece for all of them and passes `measureOutline={false}`, so a tile never
  // downloads its own outline just to measure it - not even in the moment before the board's own
  // measurement arrives. Only a tile used on its own measures its outline.
  const ownAspect = useImageAspect(measureOutline && !cellAspectProp ? outlineSrc : null);
  const cellAspect = cellAspectProp || ownAspect;
  const allDone = hasPlayer && contributors.every((u) => isDoneStatus(u.status));
  const sliced = buildSlots(contributors);
  // Board mode: the board picks ONE player per piece (`representative`, null for an empty piece) and the
  // tile shows only that player's image. Without it (a tile used on its own) several players still show as
  // a grid of portraits.
  const useRepresentative = representative !== undefined && !finalView;
  const shared = !sliced && !useRepresentative && contributors.length > 1;
  const primary = useRepresentative ? representative : contributors[0];
  const anyAutoFilled = contributors.some((u) => u.status === 'auto_filled');

  // FINAL view (reveal page): ONE image per piece - the viewer's own drawing, else a teammate's who drew,
  // else the coloured piece. Never a grid of copies. See pickRevealContributor.
  const finalAuthor = finalView && !sliced ? pickRevealContributor(contributors, currentUserId, tileId) : null;
  let finalSource = null;
  if (finalView && !sliced) {
    if (!finalAuthor) finalSource = 'master';
    else finalSource = String(finalAuthor.userId) === String(currentUserId) ? 'own' : 'other';
  }

  const names = contributorNames(contributors);
  const noneFinished = !contributors.some((u) => isDoneStatus(u.status));
  const referenceFallbackOnly = useMasterFallback && Boolean(masterSrc) && noneFinished;
  const liveHoverLabel = referenceFallbackOnly
    ? ''
    : names
      ? `${names}${anyAutoFilled && contributors.length === 1 ? ' — auto-filled' : allDone ? ' — done' : ' — drawing…'}`
      : 'Waiting for a player';
  let hoverLabel = liveHoverLabel;
  if (finalSource === 'own') hoverLabel = 'Your drawing';
  else if (finalSource === 'other') hoverLabel = `Drawn by ${finalAuthor.name || 'a teammate'}`;
  else if (finalSource === 'master') hoverLabel = '';
  else if (useRepresentative && primary && !referenceFallbackOnly) {
    const mine = currentUserId != null && String(primary.userId) === String(currentUserId);
    const state = primary.status === 'auto_filled' ? 'auto-filled' : isDoneStatus(primary.status) ? 'done' : 'drawing…';
    const others = contributors.length > 1 ? ` (+${contributors.length - 1} more on this piece)` : '';
    hoverLabel = mine ? `Your drawing${others}` : `${primary.name || 'A player'} — ${state}${others}`;
  }

  const outline = (
    <div className="live-canvas-tile-outline-wrap">
      <img
        src={outlineSrc}
        alt=""
        className={`live-canvas-tile-outline-img${hasPlayer ? '' : ' live-canvas-tile-outline-img--waiting'}`}
        aria-hidden
      />
      {!hasPlayer ? <div className="live-canvas-tile-waiting-mask" aria-hidden /> : null}
    </div>
  );

  let body;
  if (sliced) {
    body = (
      <div className="live-canvas-slots">
        {sliced.slots.map((slot) => {
          const image = slotImage(slot.rep, {
            previews,
            masterSrc,
            outlineSrc,
            useMasterFallback,
            resolveKey: s3PublicUrl,
          });
          const slotNames = contributorNames(slot.occupants);
          return (
            <div
              key={`slot-${slot.index}`}
              className={`live-canvas-slot${slot.rep && isDoneStatus(slot.rep.status) ? ' live-canvas-slot--done' : ''}`}
              data-slot={slot.index}
              title={slotNames || undefined}
              style={{
                left: `${slot.rect.x * 100}%`,
                top: `${slot.rect.y * 100}%`,
                width: `${slot.rect.w * 100}%`,
                height: `${slot.rect.h * 100}%`,
              }}
            >
              {image.src ? (
                image.crop ? (
                  <CroppedImage src={image.src} rect={slot.rect} dim={image.dim} />
                ) : (
                  <PlayerImage
                    src={image.src}
                    guard={Boolean(slot.rep && isDoneStatus(slot.rep.status))}
                    expectedAspect={cellAspect ? (cellAspect * slot.rect.w) / slot.rect.h : null}
                    className="live-canvas-tile-img"
                    fallback={
                      <CroppedImage src={masterSrc || outlineSrc} rect={slot.rect} dim={false} />
                    }
                  />
                )
              ) : null}
              {slot.occupants.length > 0 && !referenceFallbackOnly ? (
                <span className="live-canvas-portrait-name">
                  {slot.rep?.name || ''}
                  {slot.occupants.length > 1 ? ` +${slot.occupants.length - 1}` : ''}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  } else if (finalView) {
    const referenceSrc = masterSrc || outlineSrc;
    const reference = referenceSrc ? (
      <img src={referenceSrc} alt="" className="live-canvas-tile-img" aria-hidden />
    ) : null;
    if (finalAuthor && finalSource === 'own') {
      // The viewer ALWAYS sees their own drawing. No shape check here: if it does not match the piece (e.g.
      // drawn while pieces were being sliced) it is fitted whole over the coloured piece instead of being
      // swapped for it.
      body = (
        <div className="live-canvas-final-own">
          {reference}
          <img
            src={s3PublicUrl(finalAuthor.tileImageKey)}
            alt=""
            className="live-canvas-tile-img live-canvas-final-own-img"
            aria-hidden
          />
        </div>
      );
    } else if (finalAuthor) {
      body = (
        <PlayerImage
          src={s3PublicUrl(finalAuthor.tileImageKey)}
          guard
          expectedAspect={cellAspect}
          className="live-canvas-tile-img"
          fallback={reference}
        />
      );
    } else {
      body = reference || outline;
    }
  } else if (!hasPlayer) {
    body = useMasterFallback && masterSrc ? (
      <img src={masterSrc} alt="" className="live-canvas-tile-img" aria-hidden />
    ) : (
      outline
    );
  } else if (!shared) {
    const src = portraitSrc(primary, { masterSrc, useMasterFallback, previews });
    body = src ? (
      <PlayerImage
        src={src}
        guard={isDoneStatus(primary?.status)}
        expectedAspect={cellAspect}
        className="live-canvas-tile-img"
        fallback={<img src={masterSrc || outlineSrc} alt="" className="live-canvas-tile-img" aria-hidden />}
      />
    ) : (
      outline
    );
  } else {
    const { shown, hidden } = splitVisible(contributors);
    const { cols, rows } = portraitGridShape(shown.length);
    body = (
      <div
        className="live-canvas-tile-portraits"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        }}
      >
        {shown.map((u) => {
          const src = portraitSrc(u, { masterSrc, useMasterFallback, previews });
          return (
            <div
              key={`portrait-${u.userId}`}
              className={`live-canvas-portrait${isDoneStatus(u.status) ? ' live-canvas-portrait--done' : ''}`}
              title={String(u.name || '')}
            >
              <img
                src={src || outlineSrc}
                alt=""
                className={src ? 'live-canvas-tile-img' : 'live-canvas-tile-outline-img'}
                aria-hidden
              />
              {u.name ? <span className="live-canvas-portrait-name">{u.name}</span> : null}
            </div>
          );
        })}
        {hidden > 0 ? <span className="live-canvas-portrait-more">+{hidden}</span> : null}
      </div>
    );
  }

  return (
    <div
      className={`live-canvas-tile${isMine ? ' live-canvas-tile--mine' : ''}${
        allDone ? ' live-canvas-tile--done' : ''
      }${hasPlayer ? ' live-canvas-tile--active' : ''}${
        sliced ? ' live-canvas-tile--sliced' : shared && !finalView ? ' live-canvas-tile--shared' : ''
      }${finalView ? ' live-canvas-tile--final' : ''}`}
      aria-label={hoverLabel || undefined}
      data-contributors={contributors.length}
      data-final-source={finalSource || undefined}
      data-final-user={finalAuthor ? String(finalAuthor.userId) : undefined}
      data-rep-user={useRepresentative && primary ? String(primary.userId) : undefined}
    >
      {body}
      {hoverLabel ? <span className="live-canvas-tile-hover-name">{hoverLabel}</span> : null}
    </div>
  );
}
